import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { GX_API_URL } from '@/constants/env';
import {
  type ExportPreviewListFilters,
  exportPreviewOptions,
  getSymbolsQueryKey
} from '@/features/DataHub/api';
import { templatesOptions } from '@/features/DataHub/api/templates.options';
import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import { useSelectedSymbols } from '@/features/DataHub/hooks/useSelectedSymbols';
import { useExportSettings } from '@/features/DataHub/modules/Export/context/ExportSettingsContext';
import {
  apiPreviewService,
  powerQueryPreviewService,
  pythonPreviewService,
  snowflakePreviewService
} from '@/features/DataHub/modules/Export/services';
import type { ExportSettingsType } from '@/features/DataHub/modules/Export/types/context.types';
import type { ExportOptionId } from '@/features/DataHub/modules/Export/types/exportConfig.types';
import { buildPreviewContext, getFromToValues } from '@/features/DataHub/modules/Export/utils';

import useDownloadFileContent from './useDownloadFileContent';
import { useExportApiKey } from './useExportApiKey';
import { useStableDownloadHandler } from './useStableDownloadHandler';

// Fetch strategy determines what data to fetch
type FetchStrategy = 'python_template' | 'power_query_template' | 'data' | 'none';

// File type for downloads
type FileType = 'csv' | 'json' | 'python' | 'txt';

const getFetchStrategy = (optionId: string): FetchStrategy => {
  switch (optionId) {
    case 'python':
      return 'python_template';
    case 'power_query':
      return 'power_query_template';
    case 'csv':
    case 'json':
      return 'data';
    default:
      return 'none';
  }
};

const getFileType = (optionId: string): FileType => {
  switch (optionId) {
    case 'csv':
      return 'csv';
    case 'json':
      return 'json';
    case 'python':
    case 'snowflake':
      return 'python';
    default:
      return 'txt';
  }
};

type UseUniversalPreviewParams = {
  optionId: ExportOptionId;
  enabled?: boolean;
  capturePosthogEvent: () => void;
  /** Required for gx_api option - the pre-built API request string */
  exportApiRequestString?: string;
  /** Required for power_query option - the export API URL */
  exportApiUrl?: string;
  /** Optional override for export settings (otherwise uses context) */
  exportSettings?: ExportSettingsType;
};

type PreviewResult = {
  loading: boolean;
  error: boolean;
  fileContent: string;
  downloadFile: () => void;
};

/**
 * Universal preview hook that handles all export preview types.
 * Replaces useJsonCsvPreview, usePythonPreview, usePowerQueryPreview, and useApiSqlPreview.
 *
 * @example
 * ```tsx
 * const { loading, error, fileContent, downloadFile } = useUniversalPreview({
 *   optionId: 'python',
 *   capturePosthogEvent: () => posthog.capture('download')
 * });
 * ```
 */
function useUniversalPreview({
  optionId,
  enabled = true,
  capturePosthogEvent,
  exportApiRequestString = '',
  exportApiUrl = ''
}: UseUniversalPreviewParams): PreviewResult {
  // Get shared dependencies
  const { exportSettings } = useExportSettings();
  const symbols = useSelectedSymbols();
  const apiKey = useExportApiKey();
  const { timezoneValue } = useDataHubUserConfig();
  const { downloadFileContent } = useDownloadFileContent(capturePosthogEvent);

  // Determine fetch strategy
  const fetchStrategy = getFetchStrategy(optionId);
  const fileType = getFileType(optionId);

  // Build preview context (used by all services)
  const previewContext = useMemo(
    () =>
      buildPreviewContext({
        exportSettings,
        symbols,
        timezone: timezoneValue,
        apiKey,
        gxURL: GX_API_URL
      }),
    [exportSettings, symbols, timezoneValue, apiKey]
  );

  // Calculate from/to values for data queries
  const { fromValue, toValue } = getFromToValues(exportSettings);
  const symbolsKey = useMemo(() => getSymbolsQueryKey(symbols), [symbols]);

  // Data query filters (for CSV/JSON)
  const dataFilters: ExportPreviewListFilters = useMemo(
    () => ({
      symbolsKey,
      from: fromValue,
      to: toValue,
      includeMetadata: exportSettings.includeMetadata,
      exportType: exportSettings.dateRangeType,
      isCSV: optionId === 'csv',
      userTimezone: timezoneValue
    }),
    [symbolsKey, fromValue, toValue, exportSettings, optionId, timezoneValue]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Queries - only ONE runs based on fetch strategy
  // ─────────────────────────────────────────────────────────────────────────────

  // Python template query
  const pythonQuery = useQuery({
    ...templatesOptions.python(),
    enabled: enabled && fetchStrategy === 'python_template'
  });

  // Power Query template query
  const powerQueryQuery = useQuery({
    ...templatesOptions.powerQuery(),
    enabled: enabled && fetchStrategy === 'power_query_template'
  });

  // Data query (CSV/JSON)
  const dataQuery = useQuery({
    ...exportPreviewOptions.list(dataFilters, symbols),
    enabled: enabled && fetchStrategy === 'data' && symbolsKey.length > 0
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Generate content based on option ID
  // ─────────────────────────────────────────────────────────────────────────────

  const fileContent = useMemo(() => {
    if (!enabled) return '';

    switch (optionId) {
      case 'python': {
        if (!pythonQuery.data) return '';
        return pythonPreviewService.generateFromTemplate(pythonQuery.data, previewContext).content;
      }

      case 'power_query': {
        if (!powerQueryQuery.data || !exportApiUrl) return '';
        return powerQueryPreviewService.generateFromTemplate(
          powerQueryQuery.data,
          previewContext,
          exportApiUrl
        ).content;
      }

      case 'csv':
      case 'json': {
        return dataQuery.data ?? '';
      }

      case 'gx_api': {
        return exportApiRequestString;
      }

      case 'curl': {
        return apiPreviewService.generateCurlCommand(previewContext);
      }

      case 'snowflake': {
        return snowflakePreviewService.generatePreview(previewContext).content;
      }

      default:
        return '';
    }
  }, [
    enabled,
    optionId,
    pythonQuery.data,
    powerQueryQuery.data,
    dataQuery.data,
    previewContext,
    exportApiRequestString,
    exportApiUrl
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Loading and error states
  // ─────────────────────────────────────────────────────────────────────────────

  const loading = useMemo(() => {
    switch (fetchStrategy) {
      case 'python_template':
        return pythonQuery.isLoading;
      case 'power_query_template':
        return powerQueryQuery.isLoading;
      case 'data':
        return dataQuery.isLoading;
      default:
        return false;
    }
  }, [fetchStrategy, pythonQuery.isLoading, powerQueryQuery.isLoading, dataQuery.isLoading]);

  const error = useMemo(() => {
    switch (fetchStrategy) {
      case 'python_template':
        return pythonQuery.isError;
      case 'power_query_template':
        return powerQueryQuery.isError;
      case 'data':
        return dataQuery.isError;
      default:
        return false;
    }
  }, [fetchStrategy, pythonQuery.isError, powerQueryQuery.isError, dataQuery.isError]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Download handler
  // ─────────────────────────────────────────────────────────────────────────────

  const downloadFile = useStableDownloadHandler(fileContent, downloadFileContent, fileType);

  return { loading, error, fileContent, downloadFile };
}

export default useUniversalPreview;
